import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { VEHICLE_USAGE_LABEL, FUEL_TYPE_LABEL, type FuelType } from '@apleona/shared';
import { api } from '../api';
import { VehicleForm, emptyVehicle, vehicleToForm, type VehicleFormValue } from '../components/VehicleForm';
import {
  Card, Empty, KV, Loading, Plate, StatusBadge, TypeBadge, UsageBadge, VehicleStatusBadge,
  formatDate, formatDateTime, formatKm,
} from '../components/ui';

export const FahrzeugePage: React.FC = () => {
  const [q, setQ] = useState('');
  const [usage, setUsage] = useState('');
  const [form, setForm] = useState<VehicleFormValue | null>(null);
  const params = new URLSearchParams({ pageSize: '100' });
  if (q) params.set('q', q);
  if (usage) params.set('usage', usage);

  const { data, isLoading } = useQuery({
    queryKey: ['fahrzeuge', params.toString()],
    queryFn: () => api<{ total: number; items: any[] }>(`/fahrzeuge?${params}`),
  });

  return (
    <div className="grid" style={{ gap: 16 }}>
      <Card title="Filter">
        <div className="toolbar">
          <div className="field grow">
            <label>Suche</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kennzeichen, FIN, Marke …" />
          </div>
          <div className="field">
            <label>Nutzungsart</label>
            <select value={usage} onChange={(e) => setUsage(e.target.value)}>
              <option value="">alle</option>
              {Object.entries(VEHICLE_USAGE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card
        title={data ? `${data.total} Fahrzeuge` : 'Fahrzeuge'}
        bodyless
        actions={
          <button className="btn primary" onClick={() => setForm(emptyVehicle())}>
            + Neues Fahrzeug
          </button>
        }
      >
        {isLoading ? (
          <Loading />
        ) : !data || data.items.length === 0 ? (
          <Empty text="Keine Fahrzeuge gefunden" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Kennzeichen</th>
                  <th>Marke / Modell</th>
                  <th>Art</th>
                  <th>Antrieb</th>
                  <th>Standort</th>
                  <th>Aktuell bei</th>
                  <th className="num">km</th>
                  <th className="num">Vorgänge</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id}>
                    <td><Link to={`/fahrzeuge/${v.id}`}><Plate value={v.plate} /></Link></td>
                    <td>{[v.make, v.model].filter(Boolean).join(' ') || '—'}</td>
                    <td><UsageBadge usage={v.usage} /> <span className="badge grey">{v.category}</span></td>
                    <td className="small">{FUEL_TYPE_LABEL[v.fuelType as FuelType] ?? v.fuelType}</td>
                    <td className="small">{v.location?.name ?? '—'}</td>
                    <td className="small">
                      {v.currentHolder ? `${v.currentHolder.firstName ?? ''} ${v.currentHolder.lastName}`.trim() : '—'}
                    </td>
                    <td className="num">{formatKm(v.currentMileage)}</td>
                    <td className="num">{v._count.protocols}</td>
                    <td><VehicleStatusBadge status={v.status} /></td>
                    <td className="right">
                      <button className="btn sm" onClick={() => setForm(vehicleToForm(v))}>
                        Bearbeiten
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <VehicleForm open={!!form} initial={form ?? emptyVehicle()} onClose={() => setForm(null)} />
    </div>
  );
};

export const FahrzeugDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<VehicleFormValue | null>(null);
  const { data: v, isLoading } = useQuery({
    queryKey: ['fahrzeug', id],
    queryFn: () => api<any>(`/fahrzeuge/${id}`),
    enabled: !!id,
  });
  const { data: schaeden } = useQuery({
    queryKey: ['fahrzeug-schaeden', id],
    queryFn: () => api<any[]>(`/fahrzeuge/${id}/schaeden`),
    enabled: !!id,
  });

  if (isLoading || !v) return <Loading />;

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <Plate value={v.plate} />
          <h2>{[v.make, v.model].filter(Boolean).join(' ')}</h2>
          <VehicleStatusBadge status={v.status} />
          <UsageBadge usage={v.usage} />
        </div>
        <div className="row">
          <button className="btn primary" onClick={() => setForm(vehicleToForm(v))}>Bearbeiten</button>
          <Link className="btn" to="/fahrzeuge">Zurück zur Liste</Link>
        </div>
      </div>
      <VehicleForm open={!!form} initial={form ?? emptyVehicle()} onClose={() => setForm(null)} />

      <div className="grid k3">
        <Card title="Stammdaten" bodyless>
          <KV
            rows={[
              ['Kennzeichen', v.plate],
              ['FIN', v.vin ?? '—'],
              ['Klasse', v.category],
              ['Antrieb', FUEL_TYPE_LABEL[v.fuelType as FuelType] ?? v.fuelType],
              ['Budgetgruppe', v.budgetGroup ?? '—'],
              ['km-Stand', formatKm(v.currentMileage)],
            ]}
          />
        </Card>
        <Card title="Zuordnung" bodyless>
          <KV
            rows={[
              ['Gesellschaft', v.company?.name ?? '—'],
              ['Standort', v.location?.name ?? '—'],
              [
                'Aktuell bei',
                v.currentHolder ? `${v.currentHolder.firstName ?? ''} ${v.currentHolder.lastName}`.trim() : '—',
              ],
              ['HU fällig', v.huDueAt ? formatDate(v.huDueAt) : '—'],
              ['Leasingende', v.leaseEndAt ? formatDate(v.leaseEndAt) : '—'],
            ]}
          />
        </Card>
        <Card title="QR-Code für Poolerfassung">
          <p className="small muted">
            Aufkleber im Fahrzeug: Beim Scannen springt die App direkt in die Übernahme bzw. Rückgabe.
          </p>
          <div className="mono small" style={{ wordBreak: 'break-all', background: '#f6f8fa', padding: 10, borderRadius: 6 }}>
            apleona-fuhrpark://pool/{v.qrToken}
          </div>
        </Card>
      </div>

      <Card title={`Schadenshistorie (${schaeden?.length ?? 0})`} bodyless>
        {!schaeden || schaeden.length === 0 ? (
          <Empty icon="✓" text="Keine Schäden dokumentiert" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Datum</th><th>Beleg</th><th>Art</th><th>Bereich</th><th>Größe</th><th>Bemerkung</th></tr>
              </thead>
              <tbody>
                {schaeden.map((d) => (
                  <tr key={d.id}>
                    <td className="small">{formatDate(d.protocol.occurredAt)}</td>
                    <td><Link className="mono small" to={`/protokolle/${d.protocol.id}`}>{d.protocol.number}</Link></td>
                    <td className="small">{d.kind}</td>
                    <td className="small">{d.area ?? '—'}</td>
                    <td className="small">{d.sizeMm ? `${d.sizeMm} mm` : '—'}</td>
                    <td className="small">
                      {d.preexisting ? <span className="badge grey">Vorschaden</span> : null} {d.note ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Vorgänge" bodyless>
        {v.protocols.length === 0 ? (
          <Empty text="Noch keine Vorgänge" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Beleg-Nr.</th><th>Vorgang</th><th>Zeitpunkt</th>
                  <th className="num">km</th><th className="num">Sch.</th><th className="num">Fotos</th>
                  <th>Erfasst von</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {v.protocols.map((p: any) => (
                  <tr key={p.id}>
                    <td><Link className="mono" to={`/protokolle/${p.id}`}>{p.number}</Link></td>
                    <td><TypeBadge type={p.type} /></td>
                    <td>{formatDateTime(p.occurredAt)}</td>
                    <td className="num">{formatKm(p.mileage)}</td>
                    <td className="num">{p._count.damages || '—'}</td>
                    <td className="num">{p._count.photos}</td>
                    <td className="small">{p.coordinator.firstName} {p.coordinator.lastName}</td>
                    <td><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {v.trips.length > 0 ? (
        <Card title="Poolfahrten" bodyless>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Fahrer*in</th><th>Von</th><th>Bis</th><th className="num">km</th><th>Zweck</th><th>Status</th></tr>
              </thead>
              <tbody>
                {v.trips.map((t: any) => (
                  <tr key={t.id}>
                    <td>{t.driverName}</td>
                    <td>{formatDateTime(t.checkoutAt)}</td>
                    <td>{t.checkinAt ? formatDateTime(t.checkinAt) : '—'}</td>
                    <td className="num">
                      {t.mileageIn != null && t.mileageOut != null ? `${t.mileageIn - t.mileageOut} km` : '—'}
                    </td>
                    <td className="small">{t.purpose ?? '—'}</td>
                    <td>
                      <span className={`badge ${t.status === 'OFFEN' ? 'warn' : 'green'}`}>{t.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
};
